/**
 * The Skyline Gala: Dubai's headline night out, on the roof of a tower 300 m up. A ticket at the door (general or
 * VIP), formal wear or you don't get in, then the lift and the reveal: the city below, the needle tower close enough to
 * touch, the sun going down. The guests are people worth knowing: talk well and they swap numbers; a VIP guest who
 * likes you can walk you into the VIP lounge (so can enough followers, or a VIP ticket). It's status: a photo on the
 * edge does a lot for your profile, and the night goes in your story.
 */
import { cheaper } from './prices.ts';
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { AED_NGN } from './travel.ts';
import { socialOf } from './social.ts';
import { Rng } from './rng.ts';
import { nameFor } from './spawn.ts';

export const GALA = { name: 'The Skyline Gala, Al Sama Tower', short: 'the Skyline Gala', ticket: 1_200, vip: 4_500, opens: 18, closes: 25 } as const;
export const galaPrice = (vip: boolean) => cheaper((vip ? GALA.vip : GALA.ticket) * AED_NGN, 1_000);
/** followers enough to be waved into the VIP lounge on your name alone */
export const VIP_FOLLOWERS = 25_000;

interface GalaNight { day: number; vip: boolean; invited?: boolean; photo?: boolean; drinks: number; met: string[] }
interface GalaState { night?: GalaNight; nights?: number }
const of = (s: LifeState): GalaState => ((s as LifeState & { galaNights?: GalaState }).galaNights ??= {});
const day = (s: LifeState) => Math.floor((s.minutes - 6 * 60) / 1440); // (a night runs past midnight: it's still that night till 6)

/** Doors from six till one. */
export function galaOpen(s: LifeState): boolean { const h = (s.minutes / 60) % 24; return h >= GALA.opens || h < GALA.closes - 24; }
export function galaTonight(s: LifeState): GalaNight | null { const n = of(s).night; return n && n.day === day(s) ? n : null; }

export function buyGala(s: LifeState, vip: boolean): { ok: boolean; line: string } {
  const n = galaTonight(s);
  if (n && (n.vip || !vip)) return { ok: true, line: 'Your name is already on the list.' };
  const price = n ? galaPrice(true) - galaPrice(false) : galaPrice(vip);
  if (!spend(s, price, vip ? 'Skyline Gala, VIP' : 'Skyline Gala, ticket')) return { ok: false, line: "You can't afford it." };
  of(s).night = { day: day(s), vip, drinks: n?.drinks ?? 0, met: n?.met ?? [], photo: n?.photo };
  return { ok: true, line: vip ? 'A black card with your name in gold. "The VIP lounge is on the left as you come out of the lift."' : 'A wristband, a smile. "Enjoy your evening. The lift is on the right."' };
}

/** You can go through the rope: a VIP ticket, a VIP guest's invitation, or your name. */
export function vipAccess(s: LifeState): { ok: boolean; why: string } {
  const n = galaTonight(s);
  if (n?.vip) return { ok: true, why: 'ticket' };
  if (n?.invited) return { ok: true, why: 'invited' };
  if (socialOf(s).followers >= VIP_FOLLOWERS) return { ok: true, why: 'followers' };
  return { ok: false, why: '' };
}
export function enterVip(s: LifeState): string {
  const a = vipAccess(s);
  if (!a.ok) return '';
  s.history.push({ t: s.minutes, text: 'Got into the VIP lounge at the Skyline Gala.' });
  return a.why === 'followers' ? 'The host checks her tablet, looks up and smiles. "Of course. We follow you." The rope comes off its hook.' : a.why === 'invited' ? 'The host sees who you’re with and unhooks the rope without a word.' : 'The host glances at your black card and unhooks the rope.';
}

/** A drink from the bar. */
export function galaDrink(s: LifeState): { line: string; events: LifeEvent[] } {
  const n = galaTonight(s);
  if (n) n.drinks++;
  s.needs.fun = Math.min(100, s.needs.fun + 5);
  s.needs.social = Math.min(100, s.needs.social + 2);
  const lines = ['A glass of something cold and gold, a sugared rim. The barman doesn’t say the price: it’s in the ticket.', 'A mocktail with smoke curling off it, dates and saffron. Somebody beside you films it.', 'Champagne, and the bubbles catch the lights of the city.'];
  return { line: lines[(n?.drinks ?? 1) % lines.length], events: advance(s, 4) };
}

/** A photo on the edge with the tower behind you: once a night, it does a lot for your profile. */
export function galaPhoto(s: LifeState): { line: string; events: LifeEvent[] } {
  const n = galaTonight(s), soc = socialOf(s);
  if (n?.photo) return { line: 'You already have the shot. It’s a good one.', events: [] };
  if (n) n.photo = true;
  const gain = Math.round(40 + soc.followers * 0.03 + Math.random() * 60);
  soc.followers += gain;
  s.needs.fun = Math.min(100, s.needs.fun + 6);
  return { line: `The shot: you, the glass edge, the tower lit up behind you, the whole city under your feet. It goes up and it goes everywhere (${gain} new followers).`, events: [{ t: s.minutes, kind: 'social', text: `${gain} new followers` }, ...advance(s, 3)] };
}

// --- the guests ------------------------------------------------------------------------------------

export interface GalaGuest { name: string; sex: 'm' | 'f'; job: string; opener: string; topic: string; warmth: number; vip: boolean }
const JOBS = ['a property developer', 'a hotel owner', 'an airline captain', 'a tech founder who just sold her company', 'a fashion designer', 'a private banker', 'a racing driver', 'an architect who built half the Marina', 'a film producer', 'a surgeon', 'an investor', 'a jewellery designer'];
const VIP_JOBS = ['a royal family’s advisor', 'a billionaire’s son', 'a shipping heiress', 'the owner of the tower', 'a football club owner', 'a fund manager with a yacht in the Marina'];
const OPENERS = [
  (n: string) => `"First time up here? You keep looking over the edge," ${n} says, two glasses in hand, offering you one.`,
  (n: string) => `${n} steps beside you at the glass. "Every time, it gets me. You can see the sea from here, look."`,
  (n: string) => `"I don’t think we’ve met. I know everybody here, and I don’t know you," ${n} says, smiling. "That’s interesting."`,
  (n: string) => `${n} nods at your outfit. "Good choice. Half the people here are trying too hard."`,
  (n: string) => `"Are you here for business or for the view?" ${n} asks. "Everyone up here is one or the other."`,
];
const TOPICS = ['how they made their first million', 'the city twenty years ago, when this was all sand', 'a deal they almost lost', 'where they’d live if not here', 'the best meal they ever had', 'what they would do with one more life'];

/** Tonight's guests: the same people for the same night (four out on the roof, two in the VIP lounge). */
export function galaGuests(s: LifeState): GalaGuest[] {
  const r = new Rng((s.seed ^ Math.imul(day(s) + 11, 0x9e3779b1)) >>> 0), out: GalaGuest[] = [];
  const opens = [...OPENERS].sort(() => r.next() - 0.5);
  for (let i = 0; i < 6; i++) {
    const vip = i >= 4, sex: 'm' | 'f' = i % 2 === 0 ? 'f' : 'm', name = nameFor(r.chance(0.5) ? 'uae' : r.pick(['france', 'us', 'lagos', 'italy']), sex, r);
    out.push({ name, sex, job: r.pick(vip ? VIP_JOBS : JOBS), opener: opens[i % opens.length](name.split(' ')[0]), topic: r.pick(TOPICS), warmth: 0.35 + r.next() * 0.45, vip });
  }
  return out;
}

/** They came over: you talk (social and fun, and maybe they ask to swap numbers; a VIP guest who asks also invites you in), keep it short, or excuse yourself. */
export function galaTalk(s: LifeState, g: GalaGuest, how: 'chat' | 'short' | 'ignore'): { line: string; asks: boolean; invites: boolean; events: LifeEvent[] } {
  const first = g.name.split(' ')[0], events = advance(s, how === 'chat' ? 15 : 3);
  if (how === 'ignore') return { line: `You excuse yourself with a smile. ${first} is already talking to someone else.`, asks: false, invites: false, events };
  s.needs.social = Math.min(100, s.needs.social + (how === 'chat' ? 15 : 5));
  s.needs.fun = Math.min(100, s.needs.fun + (how === 'chat' ? 5 : 2));
  s.skills.social = Math.min(100, s.skills.social + (how === 'chat' ? 0.5 : 0.1));
  s.stats.strangersTalked++;
  if (how === 'short') return { line: `Pleasantries, the view, the weather. ${first} drifts on.`, asks: false, invites: false, events };
  const known = s.contacts.some((x) => x.name === g.name);
  const r = new Rng((s.seed ^ Math.imul(s.minutes + g.name.length, 0x85ebca6b)) >>> 0);
  const asks = !known && r.next() < g.warmth + s.skills.social / 220;
  const n = galaTonight(s);
  const invites = asks && !g.vip && !(n?.vip || n?.invited) && r.next() < 0.5;
  return { line: `You talk about ${g.topic}. ${first} listens like it matters.`, asks, invites, events };
}
export function galaSwap(s: LifeState, g: GalaGuest): LifeEvent[] {
  if (s.contacts.some((x) => x.name === g.name)) return [];
  s.contacts.push({ name: g.name, relation: 'Friend', closeness: g.vip ? 22 : 26, met: s.minutes });
  galaTonight(s)?.met.push(g.name);
  s.history.push({ t: s.minutes, text: `Met ${g.name} (${g.job}) at the Skyline Gala.` });
  return [{ t: s.minutes, kind: 'info', text: `${g.name} is in your Contacts now.` }];
}
/** A guest asks you into the VIP lounge with them. */
export function galaInvite(s: LifeState) { const n = galaTonight(s); if (n) n.invited = true; }

/** Down in the lift: the night goes in your story (once a night). */
export function leaveGala(s: LifeState): LifeEvent[] {
  const n = galaTonight(s), st = of(s);
  if (!n || (n as GalaNight & { left?: boolean }).left) return [];
  (n as GalaNight & { left?: boolean }).left = true;
  st.nights = (st.nights ?? 0) + 1;
  s.needs.fun = Math.min(100, s.needs.fun + 10);
  s.history.push({ t: s.minutes, text: `A night at the Skyline Gala, 300 metres above Dubai${n.met.length ? `: met ${n.met.join(', ')}` : ''}.` });
  return [{ t: s.minutes, kind: 'info', text: 'Down in the lift, ears popping. Somewhere up there the party goes on without you.' }];
}
